import { Link } from 'react-router'
import { ArrowLeft, Shield, Lock, Eye, Server, FileCheck, UserCheck, KeyRound, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '#app/components/ui/card'

export default function SecurityPage() {
  const securityFeatures = [
    {
      icon: Lock,
      title: 'End-to-End Encryptie',
      description: 'Al je data wordt versleuteld tijdens transport en opslag met industry-standard AES-256 encryptie.'
    },
    {
      icon: UserCheck,
      title: 'Two-Factor Authenticatie',
      description: 'Optionele 2FA via authenticator apps voor extra accountbeveiliging.'
    },
    {
      icon: KeyRound,
      title: 'Passkey Support',
      description: 'Moderne biometrische authenticatie voor veilig en snel inloggen.'
    },
    {
      icon: Server,
      title: 'Veilige Infrastructure',
      description: 'Gehost op enterprise-grade servers met 99.9% uptime garantie.'
    },
    {
      icon: Eye,
      title: 'Privacy by Design',
      description: 'GDPR-compliant met volledige controle over je persoonlijke gegevens.'
    },
    {
      icon: FileCheck,
      title: 'Regelmatige Audits',
      description: 'Periodieke security audits en penetratietests door externe specialisten.'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/50">
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Terug naar home
        </Link>

        <div className="space-y-12">
          <div className="text-center space-y-4">
            <Shield className="h-16 w-16 mx-auto text-primary" />
            <h1 className="text-4xl font-bold">Beveiliging & Privacy</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Je gegevens zijn veilig bij Woonplan. We nemen beveiliging serieus.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {securityFeatures.map((feature, index) => {
              const Icon = feature.icon
              return (
                <Card key={index}>
                  <CardHeader>
                    <Icon className="h-10 w-10 text-primary mb-2" />
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              )
            })}
          </div>

          <div className="bg-card rounded-lg p-8 space-y-6">
            <h2 className="text-2xl font-bold">Onze Beveiligingsprincipes</h2>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Minimale Data Collectie</h3>
                <p className="text-muted-foreground">
                  We verzamelen alleen de data die noodzakelijk is voor het functioneren van de applicatie.
                  Je hebt altijd inzicht in welke data we opslaan.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Transparante Verwerking</h3>
                <p className="text-muted-foreground">
                  We zijn open over hoe we je data gebruiken. Geen verborgen tracking of verkoop aan derden.
                  Je data wordt alleen gebruikt om je renovatieproject te ondersteunen.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Volledige Controle</h3>
                <p className="text-muted-foreground">
                  Je kunt op elk moment je data exporteren of je account verwijderen.
                  We respecteren je recht op privacy en gegevensbescherming.
                </p>
              </div>
            </div>
          </div>

          <Card className="bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500" />
                <CardTitle className="text-amber-900 dark:text-amber-100">Beveiligingsmelding</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-amber-800 dark:text-amber-200 text-sm">
                Vermoed je een beveiligingsprobleem? Neem direct contact op via{' '}
                <a href="mailto:security@woonplan.nl" className="underline font-semibold">
                  security@woonplan.nl
                </a>
                . We nemen alle meldingen serieus en reageren binnen 24 uur.
              </p>
            </CardContent>
          </Card>

          <div className="text-center space-y-4">
            <p className="text-muted-foreground">
              Voor meer informatie over onze beveiligingsmaatregelen, bekijk onze
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/tos" className="text-primary hover:underline">Algemene Voorwaarden</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/gdpr" className="text-primary hover:underline">GDPR Compliance</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}